import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  FileSearch, FileText, Image as ImageIcon, AlertTriangle, Loader2,
  ShieldAlert, Coins, Compass, Users, Truck, ClipboardCheck, ArrowRight,
  Plus, Trash2, X,
} from "lucide-react";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const SECTION_META = {
  risques: { label: "Risques", icon: ShieldAlert, color: "#E24B4A", to: "/app/risques" },
  financement_actifs: { label: "Actifs", icon: Coins, color: "#378ADD", to: "/app/financement" },
  origination_opportunites: { label: "Opportunités", icon: Compass, color: "#7F77DD", to: "/app/origination" },
  rh_postes: { label: "Postes RH", icon: Users, color: "#1D9E75", to: "/app/rh" },
  fournisseurs: { label: "Fournisseurs", icon: Truck, color: "#EF9F27", to: "/app/fournisseurs" },
  audits: { label: "Audits", icon: ClipboardCheck, color: "#A1A1AA", to: "/app/audits" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); }
  catch (_) { return iso; }
}

function fmtConfidence(c) {
  if (c == null) return null;
  const pct = Math.round(c * 100);
  if (pct >= 80) return { label: `${pct}% · élevée`, color: "#1D9E75" };
  if (pct >= 50) return { label: `${pct}% · moyenne`, color: "#EF9F27" };
  return { label: `${pct}% · faible`, color: "#A1A1AA" };
}

function fmtErr(detail) {
  if (!detail) return "Erreur.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Analyses() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/import/projects");
      setItems(r.data || []);
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); setItems([]); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (item) => {
    if (!window.confirm(`Supprimer l'analyse « ${item.filename || "sans titre"} » ?\n\nLes éléments déjà créés dans les modules ne seront pas supprimés.`)) return;
    try {
      await api.delete(`/workspace/import/projects/${item.id}`);
      await load();
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="analyses-root">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#7F77DD] noam-overline">
            <FileSearch className="w-3 h-3" /> Stratégique · Mes analyses
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Mes analyses</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Chaque document importé devient une analyse traçable : type détecté, résumé NOAM, et compteurs des éléments
            créés dans vos modules. Cliquez sur un chip pour rejoindre la donnée correspondante.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs" data-testid="analyses-create-btn">
          <Plus className="w-3.5 h-3.5" /> Nouvelle note d&apos;analyse
        </button>
      </header>

      {/* V23.15.35 — Doctrine 3Q · analyse la plus critique à traiter */}
      <ThreeQPageHeader endpoint="/workspace/analyses/top-priority" />

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="analyses-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{String(error)}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {items === null && (
        <div className="noam-card p-10 text-center text-zinc-400 text-sm" data-testid="analyses-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}

      {items && items.length === 0 && (
        <div className="noam-card p-10 text-center" data-testid="analyses-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#7F77DD]/10 border border-[#7F77DD]/30 flex items-center justify-center">
            <FileSearch className="w-6 h-6 text-[#7F77DD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">Aucune analyse pour l'instant</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Importez un PDF, un mémo, un PowerPoint ou une photo via le bouton « Importer » de la barre du haut, ou créez
            une note d'analyse manuelle pour archiver un raisonnement.
          </p>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm mt-6" data-testid="analyses-empty-create">
            <Plus className="w-4 h-4" /> Créer une note manuelle
          </button>
        </div>
      )}

      {items && items.length > 0 && (
        <ol className="space-y-3" data-testid="analyses-list">
          {items.map((it) => <AnalysisRow key={it.id} item={it} onDelete={() => remove(it)} />)}
        </ol>
      )}

      {showCreate && (
        <CreateAnalysisModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </div>
  );
}

function AnalysisRow({ item, onDelete }) {
  const conf = fmtConfidence(item.confidence);
  const counts = item.inserted_counts || {};
  const totalInserted = Object.values(counts).reduce((acc, n) => acc + (n || 0), 0);
  const IconType = item.is_image ? ImageIcon : FileText;
  return (
    <li className="noam-card p-5" data-testid={`analysis-row-${item.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[#7F77DD]/10 border border-[#7F77DD]/25 flex items-center justify-center shrink-0">
          <IconType className="w-5 h-5 text-[#7F77DD]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-zinc-100 text-sm font-medium truncate" data-testid="analysis-filename">{item.filename}</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">
              {item.document_type || "Document"}
            </span>
            {conf && (
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: `${conf.color}1A`, color: conf.color, border: `1px solid ${conf.color}40` }}>
                Confiance {conf.label}
              </span>
            )}
            {item.created_manually && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-400">Note manuelle</span>
            )}
            <span className="ml-auto text-[10px] text-zinc-500">{fmtDate(item.created_at)}</span>
            <button onClick={onDelete} className="p-1 rounded text-zinc-500 hover:text-[#E24B4A] hover:bg-white/5"
              title="Supprimer cette analyse" data-testid={`analysis-delete-${item.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {item.summary && (
            <p className="text-zinc-300 text-xs mt-2 leading-relaxed line-clamp-3" data-testid="analysis-summary">
              {item.summary}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="analysis-chips">
            {totalInserted === 0 && (
              <span className="text-[11px] text-zinc-500">Aucun élément persisté (preview uniquement)</span>
            )}
            {Object.entries(counts).map(([k, n]) => {
              if (!n) return null;
              const meta = SECTION_META[k];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <a
                  key={k} href={meta.to}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border transition-colors hover:bg-white/5"
                  style={{ borderColor: `${meta.color}40`, color: meta.color }}
                  data-testid={`analysis-chip-${k}`}
                >
                  <Icon className="w-3 h-3" /> {n} {meta.label}
                  <ArrowRight className="w-2.5 h-2.5 opacity-60" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </li>
  );
}

function CreateAnalysisModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ filename: "", document_type: "Note manuelle", summary: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/workspace/import/projects", {
        filename: form.filename.trim(),
        document_type: form.document_type.trim() || "Note manuelle",
        summary: form.summary.trim(),
      });
      onCreated();
    } catch (er) { setErr(fmtErr(er?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="analyses-create-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">Nouvelle analyse</div>
            <div className="noam-display text-base font-semibold mt-0.5">Archiver une note ou un raisonnement</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Titre *</label>
            <input required autoFocus value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })}
              placeholder="Ex : Synthèse comité du 12 mai"
              className="noam-input" data-testid="analyses-create-filename" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Type</label>
            <input value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })}
              placeholder="Note manuelle, mémo, compte-rendu…" className="noam-input" data-testid="analyses-create-type" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Résumé / contenu</label>
            <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
              rows={5} placeholder="Capturez ici votre analyse ou raisonnement…"
              className="noam-input" data-testid="analyses-create-summary" />
          </div>
          {err && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="analyses-create-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Création…</> : <><Plus className="w-3.5 h-3.5" /> Créer l'analyse</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
